import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  createSolarCommissioning,
  deleteSolarCommissioning,
  fetchSolarCommissioningById,
  fetchSolarCommissioningForms,
  fetchSolarCommissioningTemplate,
  updateSolarCommissioning
} from '../../api/forms';

const SYSTEM_SIZES = [6, 8, 10, 12, 16];
const DEFAULT_PANEL_DETAILS = { brand: '', wattageW: 0, quantity: 0, series: 0, parallel: 0, totalKw: 0 };
const DEFAULT_EQUIPMENT_DETAILS = { brand: '', model: '' };
const DEFAULT_BATTERY_DETAILS = { brand: '', model: '', voltage: '51.2V', ah: 100, quantity: 1, description: '' };

function numberValue(value) {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : 0;
}

function panelQuantity(details = {}) {
  const quantity = numberValue(details.quantity);
  const series = numberValue(details.series);
  const parallel = numberValue(details.parallel);
  return quantity || (series && parallel ? series * parallel : 0);
}

function normalizePanelDetails(details = {}) {
  const panel = {
    ...DEFAULT_PANEL_DETAILS,
    ...(details || {}),
    wattageW: numberValue(details?.wattageW),
    quantity: numberValue(details?.quantity),
    series: numberValue(details?.series),
    parallel: numberValue(details?.parallel)
  };
  panel.totalKw = Number(((panel.wattageW * panelQuantity(panel)) / 1000).toFixed(3));
  return panel;
}

function normalizeEquipmentDetails(details = {}) {
  return {
    ...DEFAULT_EQUIPMENT_DETAILS,
    ...(details || {}),
    brand: String(details?.brand || '').trim(),
    model: String(details?.model || '').trim()
  };
}

function normalizeBatteryDetails(details = {}, fallbackDescription = '') {
  return {
    ...DEFAULT_BATTERY_DETAILS,
    ...(details || {}),
    brand: String(details?.brand || '').trim(),
    model: String(details?.model || '').trim(),
    voltage: String(details?.voltage || DEFAULT_BATTERY_DETAILS.voltage).trim(),
    ah: numberValue(details?.ah) || DEFAULT_BATTERY_DETAILS.ah,
    quantity: numberValue(details?.quantity) || DEFAULT_BATTERY_DETAILS.quantity,
    description: String(details?.description || fallbackDescription || '').trim()
  };
}

function batteryDescriptionFromDetails(details = {}) {
  const battery = normalizeBatteryDetails(details);
  if (battery.description) return battery.description;
  const spec = [battery.voltage, battery.ah ? `${battery.ah}Ah` : '', battery.model].filter(Boolean).join(' ');
  return battery.quantity > 1 && spec ? `${battery.quantity} x ${spec}` : (spec || '51.2V 100Ah');
}

function imageFormatFromDataUrl(dataUrl = '') {
  return /^data:image\/jpe?g/i.test(dataUrl) ? 'JPEG' : 'PNG';
}

async function imageDimensionsFromDataUrl(dataUrl = '') {
  if (!String(dataUrl || '').startsWith('data:image/')) return null;
  return await new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve({
      width: image.naturalWidth || image.width || 1,
      height: image.naturalHeight || image.height || 1
    });
    image.onerror = () => resolve(null);
    image.src = dataUrl;
  });
}

function fitImageInsideBox(dimensions, maxWidth, maxHeight) {
  const sourceWidth = Math.max(1, dimensions?.width || maxWidth);
  const sourceHeight = Math.max(1, dimensions?.height || maxHeight);
  const scale = Math.min(maxWidth / sourceWidth, maxHeight / sourceHeight);
  return {
    width: Math.max(1, sourceWidth * scale),
    height: Math.max(1, sourceHeight * scale)
  };
}

function normalizeSystemPhotos(checklist = {}) {
  const photos = Array.isArray(checklist.systemPhotos) ? checklist.systemPhotos : [];
  return [checklist.systemPhotoDataUrl, ...photos]
    .map((photo) => String(photo || '').trim())
    .filter((photo) => photo.startsWith('data:image/'))
    .filter((photo, index, list) => list.indexOf(photo) === index)
    .slice(0, 12);
}

function formatDate(value) {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleDateString('en-PH', {
      timeZone: 'Asia/Manila',
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    });
  } catch {
    return '-';
  }
}

function toInputDate(value) {
  if (!value) return new Date().toISOString().slice(0, 10);
  try {
    return new Date(value).toISOString().slice(0, 10);
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function companyLogoSrc(company = {}) {
  return company.logo || `${window.location.origin}/logo.png`;
}

function batteryCapacityLabel(batteryDescription = '') {
  const text = String(batteryDescription || '').trim();
  const multiplied = text.match(/(\d+(?:\.\d+)?)\s*x.*?(\d+(?:\.\d+)?)\s*ah/i);
  if (multiplied) return `Capacity set to ${multiplied[1]} x ${multiplied[2]}Ah`;
  const single = text.match(/(\d+(?:\.\d+)?)\s*ah/i);
  if (single) return `Capacity set to ${single[1]}Ah`;
  return 'Capacity matches battery specification';
}

function isInverterSetupSection(section = {}) {
  return String(section.title || '').trim().toLowerCase() === 'inverter setup';
}

function isBatteryCapacityItem(item = {}) {
  return /capacity/i.test(String(item.label || '').trim());
}

function displayChecklistItemLabel(section, item, batteryDescription) {
  if (isInverterSetupSection(section) && isBatteryCapacityItem(item)) {
    return batteryCapacityLabel(batteryDescription);
  }
  return item?.label || '';
}

function syncBatteryCapacityItem(sections = [], batteryDescription = '') {
  const label = batteryCapacityLabel(batteryDescription);
  return sections.map((section) => {
    if (!isInverterSetupSection(section)) return section;
    return {
      ...section,
      items: (section.items || []).map((item) => (
        isBatteryCapacityItem(item) ? { ...item, label } : item
      ))
    };
  });
}

function normalizeChecklist(checklist) {
  const batteryDetails = normalizeBatteryDetails(checklist?.batteryDetails, checklist?.batteryDescription);
  const batteryDescription = checklist?.batteryDescription || batteryDescriptionFromDetails(batteryDetails);
  const clientSignature = { name: '', signatureDataUrl: '', signedAt: null, ...(checklist?.clientSignature || {}) };
  const installerSignature = { name: '', signatureDataUrl: '', signedAt: null, ...(checklist?.installerSignature || {}) };
  const systemPhotos = normalizeSystemPhotos(checklist);
  if (!clientSignature.name) clientSignature.name = checklist?.clientName || '';
  if (!installerSignature.name) installerSignature.name = checklist?.installerName || '';
  return {
    ...checklist,
    commissioningDate: toInputDate(checklist?.commissioningDate),
    systemSizeKw: Number(checklist?.systemSizeKw || 6),
    phaseType: checklist?.phaseType || 'Single phase',
    batteryDescription,
    panelDetails: normalizePanelDetails(checklist?.panelDetails),
    inverterDetails: normalizeEquipmentDetails(checklist?.inverterDetails),
    batteryDetails: { ...batteryDetails, description: batteryDescription },
    systemPhotoDataUrl: systemPhotos[0] || '',
    systemPhotos,
    sections: syncBatteryCapacityItem(Array.isArray(checklist?.sections) ? checklist.sections : [], batteryDescription),
    punchList: checklist?.punchList || '',
    clientSignature,
    installerSignature
  };
}

function Field({ label, value, onChange, type = 'text', textarea = false, children }) {
  return (
    <label className="block min-w-0 text-sm">
      <span className="mb-1 block text-xs font-medium text-gray-500">{label}</span>
      {children || (textarea ? (
        <textarea rows={3} value={value || ''} onChange={(event) => onChange(event.target.value)} className="min-h-24 w-full rounded border px-3 py-2 text-base sm:text-sm" />
      ) : (
        <input type={type} value={value || ''} onChange={(event) => onChange(event.target.value)} className="min-h-11 w-full rounded border px-3 py-2 text-base sm:text-sm" />
      ))}
    </label>
  );
}

function SignatureCanvas({ value, onChange, label = 'Signature', className = 'h-44 w-full sm:h-36' }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const pointRef = useRef(null);

  const getContext = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const context = canvas.getContext('2d');
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.lineWidth = 2;
    context.strokeStyle = '#111827';
    return context;
  }, []);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const ratio = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.floor(rect.width * ratio));
    canvas.height = Math.max(1, Math.floor(rect.height * ratio));
    const context = getContext();
    if (context) context.scale(ratio, ratio);
    if (value) {
      const image = new Image();
      image.onload = () => {
        const ctx = getContext();
        if (ctx) ctx.drawImage(image, 0, 0, rect.width, rect.height);
      };
      image.src = value;
    }
  }, [getContext, value]);

  useEffect(() => {
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [resizeCanvas]);

  const pointFromEvent = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
  };

  const handlePointerDown = (event) => {
    event.preventDefault();
    canvasRef.current?.setPointerCapture?.(event.pointerId);
    drawingRef.current = true;
    pointRef.current = pointFromEvent(event);
  };

  const handlePointerMove = (event) => {
    if (!drawingRef.current) return;
    event.preventDefault();
    const nextPoint = pointFromEvent(event);
    const context = getContext();
    if (!context || !pointRef.current) return;
    context.beginPath();
    context.moveTo(pointRef.current.x, pointRef.current.y);
    context.lineTo(nextPoint.x, nextPoint.y);
    context.stroke();
    pointRef.current = nextPoint;
  };

  const handlePointerUp = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    pointRef.current = null;
    const canvas = canvasRef.current;
    if (canvas) onChange(canvas.toDataURL('image/png'));
  };

  return (
    <canvas
      ref={canvasRef}
      aria-label={`${label} drawing area`}
      className={`${className} touch-none rounded border bg-white`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    />
  );
}

function SignaturePad({ label, value, onChange, readOnly = false }) {
  const [expanded, setExpanded] = useState(false);
  const clear = () => onChange('');

  if (readOnly) {
    return (
      <div className="space-y-2">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        {value ? <img src={value} alt={label} className="h-44 w-full rounded border bg-white object-contain sm:h-36" /> : <p className="text-sm text-gray-500">No signature provided.</p>}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <div className="flex gap-2">
          <button type="button" onClick={() => setExpanded(true)} className="rounded border px-2 py-1 text-xs hover:bg-gray-100">Enlarge</button>
          <button type="button" onClick={clear} className="rounded border px-2 py-1 text-xs hover:bg-gray-100">Clear</button>
        </div>
      </div>
      <SignatureCanvas label={label} value={value} onChange={onChange} />
      {expanded && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4">
          <div className="flex max-h-[calc(100svh-1rem)] w-full max-w-5xl flex-col rounded-lg bg-white p-3 shadow-xl sm:p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h3 className="text-lg font-semibold">{label}</h3>
              <button type="button" onClick={() => setExpanded(false)} className="min-h-10 rounded border px-3 py-1 text-sm hover:bg-gray-100">X</button>
            </div>
            <SignatureCanvas label={label} value={value} onChange={onChange} className="h-[60svh] w-full flex-1 sm:h-[58vh]" />
            <div className="mt-3 flex justify-end gap-2">
              <button type="button" onClick={clear} className="min-h-10 rounded border px-4 py-2 text-sm hover:bg-gray-100">Clear</button>
              <button type="button" onClick={() => setExpanded(false)} className="min-h-10 rounded bg-indigo-600 px-4 py-2 text-sm text-white">Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function commissioningPrintHtml(checklist, company = {}) {
  const logo = companyLogoSrc(company);
  const companyName = company.name || 'iConnect Home Wireless Network & Data Solution';
  const companyAddress = company.address || 'Door 1 Agunod Bldg., Lapu-Lapu street, Magugpo Poblacion, Tagum City';
  const companyPhone = company.phone || '09562755878';
  const companyEmail = company.email || 'iconnect0718@gmail.com';
  const clientSignature = checklist.clientSignature || {};
  const installerSignature = checklist.installerSignature || {};
  const panel = normalizePanelDetails(checklist.panelDetails);
  const inverter = normalizeEquipmentDetails(checklist.inverterDetails);
  const battery = normalizeBatteryDetails(checklist.batteryDetails, checklist.batteryDescription);
  const panelCount = panelQuantity(panel);
  const batteryDescription = batteryDescriptionFromDetails(battery);
  const systemPhotos = normalizeSystemPhotos(checklist);

  return `
    <section class="sheet">
      <header class="header">
        <div>
          <h1>${escapeHtml(checklist.systemSizeKw || 6)}kW Solar System Commissioning Checklist</h1>
          <p class="muted">${escapeHtml(checklist.phaseType || 'Single phase')} with Battery ${escapeHtml(batteryDescription)}</p>
          <p><strong>Checklist No.:</strong> ${escapeHtml(checklist.checklistNumber || '-')} | <strong>Status:</strong> ${escapeHtml(checklist.status || 'draft')}</p>
        </div>
        <img src="${escapeHtml(logo)}" alt="iConnect" />
      </header>

      <div class="company">
        <strong>${escapeHtml(companyName)}</strong><br />
        ${escapeHtml(companyAddress)}<br />
        ${escapeHtml(companyPhone)} | ${escapeHtml(companyEmail)}
      </div>

      <table class="summary">
        <tbody>
          <tr><th>Project Name</th><td>${escapeHtml(checklist.projectName || '-')}</td><th>Date</th><td>${escapeHtml(formatDate(checklist.commissioningDate))}</td></tr>
          <tr><th>Client Name</th><td>${escapeHtml(checklist.clientName || '-')}</td><th>Installer Name</th><td>${escapeHtml(checklist.installerName || '-')}</td></tr>
          <tr><th>Location</th><td colspan="3">${escapeHtml(checklist.location || '-')}</td></tr>
        </tbody>
      </table>

      <h2>System Equipment</h2>
      <table class="summary">
        <tbody>
          <tr>
            <th>Panels</th>
            <td>${escapeHtml(panel.brand || '-')} | ${escapeHtml(panel.wattageW || '-')}W x ${escapeHtml(panelCount || '-')} pcs | ${escapeHtml(panel.series || '-')}S / ${escapeHtml(panel.parallel || '-')}P | ${escapeHtml(panel.totalKw || 0)}kW</td>
            <th>Inverter</th>
            <td>${escapeHtml([inverter.brand, inverter.model].filter(Boolean).join(' ') || '-')}</td>
          </tr>
          <tr>
            <th>Battery</th>
            <td colspan="3">${escapeHtml([battery.brand, batteryDescription].filter(Boolean).join(' | ') || '-')}</td>
          </tr>
        </tbody>
      </table>

      ${systemPhotos.length ? `<h2>System Photo${systemPhotos.length > 1 ? 's' : ''}</h2><div class="system-photos">${systemPhotos.map((photo, index) => `<div class="system-photo"><img src="${escapeHtml(photo)}" alt="System ${index + 1}" /></div>`).join('')}</div>` : ''}

      ${(checklist.sections || []).map((section, sectionIndex) => `
        <h2>${sectionIndex + 1}. ${escapeHtml(section.title || '')}</h2>
        <table>
          <tbody>
            ${(section.items || []).map((item) => `
              <tr>
                <td class="check">${item.checked ? '[x]' : '[ ]'}</td>
                <td>${escapeHtml(displayChecklistItemLabel(section, item, checklist.batteryDescription))}${item.note ? `<br /><span class="muted">Note: ${escapeHtml(item.note)}</span>` : ''}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      `).join('')}

      ${checklist.notes ? `<h2>General Notes</h2><p>${escapeHtml(checklist.notes)}</p>` : ''}

      <h2>Client Acceptance & Sign-Off</h2>
      <div class="signatures">
        <div>
          <div class="signature-line">${clientSignature.signatureDataUrl ? `<img src="${escapeHtml(clientSignature.signatureDataUrl)}" alt="Client signature" />` : ''}</div>
          <strong>${escapeHtml(clientSignature.name || checklist.clientName || '')}</strong>
          <small>Client / Customer</small>
        </div>
        <div>
          <div class="signature-line">${installerSignature.signatureDataUrl ? `<img src="${escapeHtml(installerSignature.signatureDataUrl)}" alt="Installer signature" />` : ''}</div>
          <strong>${escapeHtml(installerSignature.name || checklist.installerName || '')}</strong>
          <small>Installer / iConnect representative</small>
        </div>
      </div>

      <footer>${escapeHtml(companyName)} | ${escapeHtml(checklist.checklistNumber || '')}</footer>
    </section>
  `;
}

async function imageUrlToDataUrl(url) {
  if (String(url || '').startsWith('data:image/')) return url;
  try {
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) return '';
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return '';
  }
}

async function fileToCompressedDataUrl(file, maxSide = 1400, quality = 0.78) {
  if (!file) return '';
  const rawDataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  return await new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      const scale = Math.min(1, maxSide / Math.max(image.width, image.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(image.width * scale));
      canvas.height = Math.max(1, Math.round(image.height * scale));
      const context = canvas.getContext('2d');
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    image.onerror = () => resolve(rawDataUrl);
    image.src = rawDataUrl;
  });
}

async function prepareLogoForPdf(dataUrl) {
  if (!String(dataUrl || '').startsWith('data:image/')) return '';
  return await new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      const source = document.createElement('canvas');
      source.width = Math.max(1, image.naturalWidth || image.width);
      source.height = Math.max(1, image.naturalHeight || image.height);
      const sourceContext = source.getContext('2d');
      sourceContext.drawImage(image, 0, 0, source.width, source.height);

      const pixels = sourceContext.getImageData(0, 0, source.width, source.height).data;
      let minX = source.width;
      let minY = source.height;
      let maxX = 0;
      let maxY = 0;
      for (let y = 0; y < source.height; y += 1) {
        for (let x = 0; x < source.width; x += 1) {
          const index = ((y * source.width) + x) * 4;
          const alpha = pixels[index + 3];
          const red = pixels[index];
          const green = pixels[index + 1];
          const blue = pixels[index + 2];
          const isVisible = alpha > 24 && !(red > 248 && green > 248 && blue > 248);
          if (isVisible) {
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
          }
        }
      }

      if (minX > maxX || minY > maxY) {
        resolve(dataUrl);
        return;
      }

      const pad = 4;
      const sx = Math.max(0, minX + 2);
      const sy = Math.max(0, minY - pad);
      const sw = Math.min(source.width - sx, (maxX - sx) + pad);
      const sh = Math.min(source.height - sy, (maxY - minY) + (pad * 2));
      const output = document.createElement('canvas');
      output.width = Math.max(1, sw);
      output.height = Math.max(1, sh);
      const outputContext = output.getContext('2d');
      outputContext.fillStyle = '#ffffff';
      outputContext.fillRect(0, 0, output.width, output.height);
      outputContext.drawImage(source, sx, sy, sw, sh, 0, 0, output.width, output.height);
      resolve(output.toDataURL('image/png'));
    };
    image.onerror = () => resolve(dataUrl);
    image.src = dataUrl;
  });
}

function pdfFileName(checklist) {
  const cleanPart = (value, fallback) => String(value || fallback)
    .trim()
    .replace(/[^a-z0-9_-]+/gi, '-')
    .replace(/^-+|-+$/g, '');
  return [
    cleanPart(checklist?.checklistNumber, 'solar-commissioning'),
    cleanPart(checklist?.projectName, 'project'),
    cleanPart(checklist?.clientName, 'client')
  ].filter(Boolean).join('-') + '.pdf';
}

function completionCertificateFileName(checklist) {
  const cleanPart = (value, fallback) => String(value || fallback)
    .trim()
    .replace(/[^a-z0-9_-]+/gi, '-')
    .replace(/^-+|-+$/g, '');
  return [
    cleanPart(checklist?.checklistNumber, 'solar-completion-certificate'),
    cleanPart(checklist?.projectName, 'project'),
    'acceptance-completion-certificate'
  ].filter(Boolean).join('-') + '.pdf';
}

async function buildChecklistPdf(checklist, company = {}) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'portrait' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 42;
  const logo = await prepareLogoForPdf(await imageUrlToDataUrl(companyLogoSrc(company)));
  const companyName = company.name || 'iConnect Home Wireless Network & Data Solution';
  const companyAddress = company.address || 'Door 1 Agunod Bldg., Lapu-Lapu street, Magugpo Poblacion, Tagum City';
  const companyPhone = company.phone || '09562755878';
  const companyEmail = company.email || 'iconnect0718@gmail.com';
  const panel = normalizePanelDetails(checklist.panelDetails);
  const inverter = normalizeEquipmentDetails(checklist.inverterDetails);
  const battery = normalizeBatteryDetails(checklist.batteryDetails, checklist.batteryDescription);
  const panelCount = panelQuantity(panel);
  const batteryDescription = batteryDescriptionFromDetails(battery);
  const systemPhotos = normalizeSystemPhotos(checklist);
  const logoWidth = 132;
  const logoHeight = 96;
  const logoX = pageWidth - margin - logoWidth;
  const titleWidth = pageWidth - (margin * 2) - logoWidth - 20;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  const titleLines = doc.splitTextToSize(`${checklist.systemSizeKw || 6}kW Solar System Commissioning Checklist`, titleWidth);
  doc.text(titleLines, margin, 48);
  const titleBottom = 48 + ((titleLines.length - 1) * 14);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(`${checklist.phaseType || 'Single phase'} with Battery ${batteryDescription}`, margin, titleBottom + 16);
  doc.text(`Checklist No.: ${checklist.checklistNumber || '-'} | Status: ${checklist.status || 'draft'}`, margin, titleBottom + 30);
  if (logo) doc.addImage(logo, 'PNG', logoX, 30, logoWidth, logoHeight, undefined, 'FAST');

  doc.setFont('helvetica', 'bold');
  doc.text(companyName, margin, 102);
  doc.setFont('helvetica', 'normal');
  doc.text(companyAddress, margin, 116);
  doc.text(`${companyPhone} | ${companyEmail}`, margin, 130);

  autoTable(doc, {
    startY: 148,
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 4, lineColor: [209, 213, 219], lineWidth: 0.5 },
    headStyles: { fillColor: [243, 244, 246], textColor: [17, 24, 39] },
    body: [
      ['Project Name', checklist.projectName || '-', 'Date', formatDate(checklist.commissioningDate)],
      ['Client Name', checklist.clientName || '-', 'Installer Name', checklist.installerName || '-'],
      ['Location', checklist.location || '-', 'System', `${checklist.systemSizeKw || 6}kW ${checklist.phaseType || 'Single phase'}`]
    ]
  });

  let y = doc.lastAutoTable.finalY + 14;
  autoTable(doc, {
    startY: y,
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 4, lineColor: [209, 213, 219], lineWidth: 0.5 },
    headStyles: { fillColor: [243, 244, 246], textColor: [17, 24, 39] },
    body: [
      ['Panels', `${panel.brand || '-'} | ${panel.wattageW || '-'}W x ${panelCount || '-'} pcs | ${panel.series || '-'}S / ${panel.parallel || '-'}P | ${panel.totalKw || 0}kW`],
      ['Inverter', [inverter.brand, inverter.model].filter(Boolean).join(' ') || '-'],
      ['Battery', [battery.brand, batteryDescription].filter(Boolean).join(' | ') || '-']
    ]
  });

  y = doc.lastAutoTable.finalY + 14;
  if (systemPhotos.length) {
    const photoGap = 10;
    const photoColumns = systemPhotos.length > 1 ? 2 : 1;
    const maxImageWidth = (pageWidth - (margin * 2) - (photoGap * (photoColumns - 1))) / photoColumns;
    const maxImageHeight = systemPhotos.length > 1 ? 145 : 170;
    if (y + 18 > pageHeight - 48) {
      doc.addPage();
      y = 42;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(`System Photo${systemPhotos.length > 1 ? 's' : ''}`, margin, y);
    y += 8;
    let rowHeight = 0;
    for (let index = 0; index < systemPhotos.length; index += 1) {
      const photo = systemPhotos[index];
      const imageDimensions = await imageDimensionsFromDataUrl(photo);
      const fittedImage = fitImageInsideBox(imageDimensions, maxImageWidth, maxImageHeight);
      const column = index % photoColumns;
      if (column === 0 && y + fittedImage.height > pageHeight - 48) {
        doc.addPage();
        y = 42;
        rowHeight = 0;
      }
      if (y + fittedImage.height > pageHeight - 48) {
        doc.addPage();
        y = 42;
        rowHeight = 0;
      }
      doc.addImage(photo, imageFormatFromDataUrl(photo), margin + (column * (maxImageWidth + photoGap)), y, fittedImage.width, fittedImage.height, undefined, 'FAST');
      rowHeight = Math.max(rowHeight, fittedImage.height);
      if (column === photoColumns - 1 || index === systemPhotos.length - 1) {
        y += rowHeight + 10;
        rowHeight = 0;
      }
    }
    y += 4;
  }

  (checklist.sections || []).forEach((section, sectionIndex) => {
    if (y > 680) {
      doc.addPage();
      y = 42;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(`${sectionIndex + 1}. ${section.title || ''}`, margin, y);
    y += 6;
    autoTable(doc, {
      startY: y,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 3.5, lineColor: [209, 213, 219], lineWidth: 0.4 },
      columnStyles: { 0: { cellWidth: 28, halign: 'center' } },
      body: (section.items || []).map((item) => [
        item.checked ? '[x]' : '[ ]',
        item.note
          ? `${displayChecklistItemLabel(section, item, checklist.batteryDescription)}\nNote: ${item.note}`
          : displayChecklistItemLabel(section, item, checklist.batteryDescription)
      ])
    });
    y = doc.lastAutoTable.finalY + 12;
  });

  if (checklist.notes) {
    if (y > 700) {
      doc.addPage();
      y = 42;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('General Notes', margin, y);
    y += 14;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(doc.splitTextToSize(checklist.notes, pageWidth - (margin * 2)), margin, y);
    y += 36;
  }

  if (y > 610) {
    doc.addPage();
    y = 42;
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Client Acceptance & Sign-Off', margin, y);
  y += 18;
  const sigWidth = 190;
  const sigHeight = 70;
  const rightX = pageWidth - margin - sigWidth;
  const clientSig = checklist.clientSignature || {};
  const installerSig = checklist.installerSignature || {};
  if (clientSig.signatureDataUrl) doc.addImage(clientSig.signatureDataUrl, 'PNG', margin + 10, y, sigWidth - 20, sigHeight - 8);
  if (installerSig.signatureDataUrl) doc.addImage(installerSig.signatureDataUrl, 'PNG', rightX + 10, y, sigWidth - 20, sigHeight - 8);
  doc.line(margin, y + sigHeight, margin + sigWidth, y + sigHeight);
  doc.line(rightX, y + sigHeight, rightX + sigWidth, y + sigHeight);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text(clientSig.name || checklist.clientName || '', margin, y + sigHeight + 14);
  doc.text(installerSig.name || checklist.installerName || '', rightX, y + sigHeight + 14);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('Client / Customer', margin, y + sigHeight + 27);
  doc.text('Installer / iConnect representative', rightX, y + sigHeight + 27);

  const pageCount = doc.internal.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFontSize(7);
    doc.setTextColor(75, 85, 99);
    doc.text(`${companyName} | ${checklist.checklistNumber || ''}`, margin, 812);
    doc.text(`Page ${page} of ${pageCount}`, pageWidth - margin - 45, 812);
  }
  return doc;
}

async function buildCompletionCertificatePdf(checklist, company = {}) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'portrait' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 48;
  const contentWidth = pageWidth - (margin * 2);
  const logo = await prepareLogoForPdf(await imageUrlToDataUrl(companyLogoSrc(company)));
  const companyName = company.name || 'iConnect Home Wireless Network & Data Solution';
  const companyAddress = company.address || 'Door 1 Agunod Bldg., Lapu-Lapu street, Magugpo Poblacion, Tagum City';
  const companyPhone = company.phone || '09562755878';
  const companyEmail = company.email || 'iconnect0718@gmail.com';
  const panel = normalizePanelDetails(checklist.panelDetails);
  const inverter = normalizeEquipmentDetails(checklist.inverterDetails);
  const battery = normalizeBatteryDetails(checklist.batteryDetails, checklist.batteryDescription);
  const panelCount = panelQuantity(panel);
  const batteryDescription = batteryDescriptionFromDetails(battery);
  const certificateNo = String(checklist.checklistNumber || '').replace(/^SC/i, 'IC-SOLAR-CA') || 'IC-SOLAR-CA';
  const completionDate = checklist.completedAt || checklist.updatedAt || checklist.commissioningDate || new Date();
  const clientSig = checklist.clientSignature || {};
  const installerSig = checklist.installerSignature || {};
  const logoWidth = 132;
  const logoHeight = 96;
  const logoX = pageWidth - margin - logoWidth;
  const titleWidth = contentWidth - logoWidth - 20;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  const titleLines = doc.splitTextToSize('CERTIFICATE OF ACCEPTANCE AND COMPLETION', titleWidth);
  doc.text(titleLines, margin, 54);
  doc.setFontSize(11);
  doc.text('Solar Power System Project Turnover Certificate', margin, 74 + ((titleLines.length - 1) * 16));
  if (logo) doc.addImage(logo, 'PNG', logoX, 34, logoWidth, logoHeight, undefined, 'FAST');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text(companyName, margin, 102);
  doc.setFont('helvetica', 'normal');
  doc.text(companyAddress, margin, 116);
  doc.text(`${companyPhone} | ${companyEmail}`, margin, 130);

  autoTable(doc, {
    startY: 150,
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 4, lineColor: [209, 213, 219], lineWidth: 0.5 },
    headStyles: { fillColor: [243, 244, 246], textColor: [17, 24, 39] },
    body: [
      ['Client / Owner', checklist.clientName || '-', 'Completion Date', formatDate(completionDate)],
      ['Project Location', checklist.location || '-', 'Certificate No.', certificateNo],
      ['Contract Reference', checklist.projectName || checklist.checklistNumber || 'iConnect Solar Power System Proposal', 'Status', checklist.status || 'draft'],
      ['Project Description', `Supply, installation, testing, and commissioning of a ${checklist.systemSizeKw || 6}kW ${checklist.phaseType || 'Single phase'} Hybrid Solar Power System`, 'Checklist No.', checklist.checklistNumber || '-']
    ]
  });

  let y = doc.lastAutoTable.finalY + 18;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('COMPLETION CERTIFICATION', margin, y);
  y += 15;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const certification = `This is to certify that ${companyName} has completed the above-described solar power system project, including the supply, installation, testing, and commissioning works, in accordance with the agreed project scope and specifications. The system has been turned over to the Client for normal use and operation, subject to the warranty terms and any written punch-list items stated below.`;
  const certificationLines = doc.splitTextToSize(certification, contentWidth);
  doc.text(certificationLines, margin, y);
  y += (certificationLines.length * 11) + 14;

  doc.setFont('helvetica', 'bold');
  doc.text('INSTALLED SYSTEM SCOPE', margin, y);
  y += 8;
  autoTable(doc, {
    startY: y,
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 4, lineColor: [209, 213, 219], lineWidth: 0.5 },
    body: [
      ['Panels', `${panel.brand || '-'} | ${panel.wattageW || '-'}W x ${panelCount || '-'} pcs | ${panel.series || '-'}S / ${panel.parallel || '-'}P | ${panel.totalKw || 0}kW`],
      ['Inverter', [inverter.brand, inverter.model].filter(Boolean).join(' ') || '-'],
      ['Battery', [battery.brand, batteryDescription].filter(Boolean).join(' | ') || '-'],
      ['Included works', 'Mounting, PV wiring, AC/DC protection, grounding, testing, commissioning, and turnover']
    ]
  });
  y = doc.lastAutoTable.finalY + 18;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('CLIENT ACCEPTANCE', margin, y);
  y += 15;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const acceptance = 'By signing this Certificate, the Client acknowledges that the project has been inspected, completed, and accepted for turnover. This acceptance does not waive applicable product warranties, manufacturer warranties, or iConnect workmanship warranty, and shall not prevent the Client from reporting valid warranty-related concerns within the warranty period.';
  const acceptanceLines = doc.splitTextToSize(acceptance, contentWidth);
  doc.text(acceptanceLines, margin, y);
  y += (acceptanceLines.length * 11) + 12;

  autoTable(doc, {
    startY: y,
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 4, lineColor: [209, 213, 219], lineWidth: 0.5 },
    body: [
      ['Punch-list / Pending Items', checklist.punchList || 'None / As listed below: ________________________________________________'],
      ['Project Turnover Remarks', checklist.notes || 'System completed, tested, and turned over for normal use.'],
      ['Client Orientation Conducted', `Yes / No     Date: ${formatDate(completionDate)}`]
    ]
  });
  y = doc.lastAutoTable.finalY + 20;

  if (y > pageHeight - 185) {
    doc.addPage();
    y = margin;
  }
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(`Issued this ${formatDate(completionDate)} at Tagum City, Philippines.`, margin, y);
  y += 26;

  const sigWidth = 210;
  const sigHeight = 64;
  const rightX = pageWidth - margin - sigWidth;
  if (clientSig.signatureDataUrl) doc.addImage(clientSig.signatureDataUrl, 'PNG', margin + 12, y, sigWidth - 24, sigHeight - 8);
  if (installerSig.signatureDataUrl) doc.addImage(installerSig.signatureDataUrl, 'PNG', rightX + 12, y, sigWidth - 24, sigHeight - 8);
  doc.line(margin, y + sigHeight, margin + sigWidth, y + sigHeight);
  doc.line(rightX, y + sigHeight, rightX + sigWidth, y + sigHeight);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text(clientSig.name || checklist.clientName || '', margin, y + sigHeight + 14);
  doc.text(installerSig.name || checklist.installerName || 'AUTHORIZED REPRESENTATIVE', rightX, y + sigHeight + 14);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('Client / Owner', margin, y + sigHeight + 27);
  doc.text(companyName, rightX, y + sigHeight + 27);
  doc.text(`Date: ${formatDate(clientSig.signedAt || completionDate)}`, margin, y + sigHeight + 42);
  doc.text(`Date: ${formatDate(installerSig.signedAt || completionDate)}`, rightX, y + sigHeight + 42);

  const pageCount = doc.internal.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFontSize(7);
    doc.setTextColor(75, 85, 99);
    doc.text(`${companyName} | Tagum City, Philippines | ${companyPhone} | ${companyEmail}`, margin, 812);
    doc.text(`Page ${page} of ${pageCount}`, pageWidth - margin - 45, 812);
  }
  return doc;
}

function printChecklist(checklist, company) {
  const popup = window.open('', '_blank');
  if (!popup) return;
  popup.document.write(`
    <html>
      <head>
        <title>${escapeHtml(checklist.checklistNumber || 'Solar Commissioning')}</title>
        <style>
          @page { size: A4 portrait; margin: 0; }
          * { box-sizing: border-box; }
          body { margin: 0; background: #f3f4f6; color: #111827; font-family: Arial, sans-serif; font-size: 10pt; line-height: 1.3; }
          .sheet { width: 210mm; min-height: 297mm; margin: 0 auto; padding: 13mm 15mm; background: #fff; }
          .header { display: flex; justify-content: space-between; gap: 16px; border-bottom: 1px solid #9ca3af; padding-bottom: 8px; margin-bottom: 10px; }
          .header > div { flex: 1 1 auto; min-width: 0; padding-right: 8mm; }
          .header img { flex: 0 0 auto; width: 38mm; height: 28mm; object-fit: fill; }
          h1 { font-size: 15pt; margin: 0 0 4px; text-transform: uppercase; }
          h2 { font-size: 10.5pt; margin: 10px 0 4px; }
          p { margin: 0 0 6px; }
          .company { margin-bottom: 8px; }
          .muted { color: #4b5563; }
          table { width: 100%; border-collapse: collapse; margin: 4px 0 7px; page-break-inside: avoid; }
          th, td { border: 1px solid #d1d5db; padding: 4px 5px; vertical-align: top; text-align: left; }
          th { background: #f3f4f6; font-weight: 700; }
          .check { width: 32px; text-align: center; font-family: monospace; }
          .system-photos { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; align-items: start; }
          .system-photo { margin: 4px 0 8px; text-align: left; page-break-inside: avoid; }
          .system-photo img { display: block; width: auto; height: auto; max-width: 100%; max-height: 62mm; margin: 0; object-fit: contain; border: 1px solid #d1d5db; }
          .signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin: 18px 0 10px; }
          .signature-line { height: 28mm; border-bottom: 1px solid #111827; display: flex; align-items: end; justify-content: center; }
          .signature-line img { max-height: 26mm; max-width: 100%; object-fit: contain; }
          .signatures small { display: block; color: #374151; }
          footer { margin-top: 14px; padding-top: 6px; border-top: 1px solid #d1d5db; font-size: 8pt; color: #4b5563; }
          @media print { body { background: #fff; } .sheet { margin: 0; } }
        </style>
      </head>
      <body>${commissioningPrintHtml(checklist, company)}</body>
    </html>
  `);
  popup.document.close();
  window.setTimeout(() => popup.print(), 350);
}

export default function SolarCommissioningManager({ authConfig, onUnauthorized, company, initialOpenId, employeeMode = false }) {
  const [checklists, setChecklists] = useState([]);
  const [selected, setSelected] = useState(null);
  const [activeView, setActiveView] = useState('list');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [creatingSize, setCreatingSize] = useState(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const lastInitialOpenId = useRef('');
  const saveInFlight = useRef(false);
  const pendingPhotoUploads = useRef(0);
  const readOnly = employeeMode && Boolean(selected?._id);
  const editorLocked = readOnly || (employeeMode && saving);

  const loadChecklists = useCallback(async () => {
    setLoading(true);
    try {
      const list = await fetchSolarCommissioningForms(search.trim() ? { reference: search.trim() } : {}, authConfig);
      setChecklists(list);
      setError('');
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.(err);
      else setError(err?.response?.data?.error || 'Failed to load solar commissioning checklists.');
    } finally {
      setLoading(false);
    }
  }, [authConfig, onUnauthorized, search]);

  useEffect(() => {
    loadChecklists();
  }, [loadChecklists]);

  useEffect(() => {
    let objectUrl = '';
    let cancelled = false;
    const isPdfPreview = activeView === 'preview' || activeView === 'certificatePreview';
    if (!isPdfPreview || !selected) {
      setPreviewPdfUrl('');
      setPreviewLoading(false);
      return undefined;
    }

    setPreviewLoading(true);
    setPreviewPdfUrl('');
    const buildPdf = activeView === 'certificatePreview' ? buildCompletionCertificatePdf : buildChecklistPdf;
    buildPdf(selected, company)
      .then((doc) => {
        if (cancelled) return;
        const blob = doc.output('blob');
        objectUrl = URL.createObjectURL(blob);
        setPreviewPdfUrl(objectUrl);
        setError('');
      })
      .catch(() => {
        if (!cancelled) setError('Failed to build A4 PDF preview.');
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [activeView, selected, company]);

  const openChecklist = useCallback(async (id) => {
    setLoading(true);
    try {
      const data = await fetchSolarCommissioningById(id, authConfig);
      setSelected(normalizeChecklist(data));
      setActiveView('edit');
      setError('');
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.(err);
      else setError(err?.response?.data?.error || 'Failed to open commissioning checklist.');
    } finally {
      setLoading(false);
    }
  }, [authConfig, onUnauthorized]);

  useEffect(() => {
    if (!initialOpenId || lastInitialOpenId.current === initialOpenId) return;
    lastInitialOpenId.current = initialOpenId;
    openChecklist(initialOpenId);
  }, [initialOpenId, openChecklist]);

  const newChecklist = async (systemSizeKw = 6) => {
    setCreatingSize(systemSizeKw);
    try {
      const data = employeeMode
        ? await fetchSolarCommissioningTemplate(systemSizeKw, authConfig)
        : await createSolarCommissioning({ systemSizeKw, phaseType: 'Single phase' }, authConfig);
      setSelected(normalizeChecklist(data));
      setActiveView('edit');
      if (!employeeMode) await loadChecklists();
      setMessage('');
      setError('');
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.(err);
      else setError(err?.response?.data?.error || 'Failed to create commissioning checklist.');
    } finally {
      setCreatingSize(null);
    }
  };

  const changeSelected = (updater) => setSelected((current) => (
    !current || (employeeMode && (current._id || saveInFlight.current)) ? current : updater(current)
  ));

  const patchSelected = (path, value) => {
    changeSelected((current) => {
      const next = { ...current };
      const keys = path.split('.');
      let target = next;
      for (let index = 0; index < keys.length - 1; index += 1) {
        target[keys[index]] = { ...(target[keys[index]] || {}) };
        target = target[keys[index]];
      }
      target[keys[keys.length - 1]] = value;
      if (path === 'clientName') {
        const previousProjectName = current.clientName || '';
        const signatureName = current.clientSignature?.name || '';
        if (!signatureName || signatureName === previousProjectName) {
          next.clientSignature = { ...(next.clientSignature || {}), name: value };
        }
      }
      if (path === 'installerName') {
        const previousProjectName = current.installerName || '';
        const signatureName = current.installerSignature?.name || '';
        if (!signatureName || signatureName === previousProjectName) {
          next.installerSignature = { ...(next.installerSignature || {}), name: value };
        }
      }
      if (path === 'batteryDescription') {
        next.batteryDetails = normalizeBatteryDetails(next.batteryDetails, value);
        next.batteryDetails.description = value;
        next.sections = syncBatteryCapacityItem(next.sections || [], value);
      }
      if (path.startsWith('panelDetails.')) {
        next.panelDetails = normalizePanelDetails(next.panelDetails);
      }
      if (path.startsWith('batteryDetails.')) {
        next.batteryDetails.description = '';
        next.batteryDetails = normalizeBatteryDetails(next.batteryDetails);
        next.batteryDescription = batteryDescriptionFromDetails(next.batteryDetails);
        next.batteryDetails.description = next.batteryDescription;
        next.sections = syncBatteryCapacityItem(next.sections || [], next.batteryDescription);
      }
      return next;
    });
  };

  const patchSignatureImage = (signatureKey, value) => {
    changeSelected((current) => ({
      ...current,
      [signatureKey]: {
        ...(current?.[signatureKey] || {}),
        signatureDataUrl: value,
        signedAt: value ? (current?.[signatureKey]?.signedAt || new Date().toISOString()) : null
      }
    }));
  };

  const handleSystemPhotoChange = async (event) => {
    if (editorLocked) return;
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    pendingPhotoUploads.current += 1;
    setUploadingPhotos(true);
    try {
      const dataUrls = await Promise.all(files.map((file) => fileToCompressedDataUrl(file)));
      changeSelected((current) => {
        const systemPhotos = normalizeSystemPhotos({
          ...current,
          systemPhotos: [...normalizeSystemPhotos(current), ...dataUrls]
        });
        return {
          ...current,
          systemPhotoDataUrl: systemPhotos[0] || '',
          systemPhotos
        };
      });
      setError('');
    } catch {
      setError('Failed to load one or more system photos.');
    } finally {
      pendingPhotoUploads.current -= 1;
      setUploadingPhotos(pendingPhotoUploads.current > 0);
      event.target.value = '';
    }
  };

  const removeSystemPhoto = (photoIndex) => {
    changeSelected((current) => {
      const systemPhotos = normalizeSystemPhotos(current).filter((_, index) => index !== photoIndex);
      return {
        ...current,
        systemPhotoDataUrl: systemPhotos[0] || '',
        systemPhotos
      };
    });
  };

  const clearSystemPhotos = () => {
    changeSelected((current) => ({
      ...current,
      systemPhotoDataUrl: '',
      systemPhotos: []
    }));
  };

  const updateChecklistItem = (sectionIndex, itemIndex, field, value) => {
    changeSelected((current) => ({
      ...current,
      sections: (current.sections || []).map((section, sIndex) => (
        sIndex !== sectionIndex ? section : {
          ...section,
          items: (section.items || []).map((item, iIndex) => (
            iIndex === itemIndex ? { ...item, [field]: value } : item
          ))
        }
      ))
    }));
  };

  const saveChecklist = async () => {
    if (!selected || readOnly || saveInFlight.current || (employeeMode && pendingPhotoUploads.current) || (!employeeMode && !selected._id)) return;
    saveInFlight.current = true;
    setSaving(true);
    try {
      const payload = {
        ...selected,
        panelDetails: normalizePanelDetails(selected.panelDetails),
        inverterDetails: normalizeEquipmentDetails(selected.inverterDetails),
        batteryDetails: normalizeBatteryDetails(selected.batteryDetails, selected.batteryDescription),
        batteryDescription: batteryDescriptionFromDetails(selected.batteryDetails),
        systemPhotoDataUrl: normalizeSystemPhotos(selected)[0] || '',
        systemPhotos: normalizeSystemPhotos(selected),
        sections: syncBatteryCapacityItem(selected.sections || [], batteryDescriptionFromDetails(selected.batteryDetails)),
        clientSignature: {
          ...(selected.clientSignature || {}),
          name: selected.clientSignature?.name || selected.clientName || '',
          signedAt: selected.clientSignature?.signatureDataUrl
            ? (selected.clientSignature?.signedAt || new Date().toISOString())
            : null
        },
        installerSignature: {
          ...(selected.installerSignature || {}),
          name: selected.installerSignature?.name || selected.installerName || '',
          signedAt: selected.installerSignature?.signatureDataUrl
            ? (selected.installerSignature?.signedAt || new Date().toISOString())
            : null
        }
      };
      const saved = employeeMode
        ? await createSolarCommissioning(payload, authConfig)
        : await updateSolarCommissioning(selected._id, payload, authConfig);
      setSelected(normalizeChecklist(saved));
      await loadChecklists();
      setMessage('Commissioning checklist saved.');
      setError('');
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.(err);
      else setError(err?.response?.data?.error || 'Failed to save commissioning checklist.');
    } finally {
      saveInFlight.current = false;
      setSaving(false);
    }
  };

  const removeChecklist = async () => {
    if (employeeMode || !selected?._id || !window.confirm('Delete this commissioning checklist?')) return;
    setSaving(true);
    try {
      await deleteSolarCommissioning(selected._id, authConfig);
      setSelected(null);
      setActiveView('list');
      await loadChecklists();
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.(err);
      else setError(err?.response?.data?.error || 'Failed to delete commissioning checklist.');
    } finally {
      setSaving(false);
    }
  };

  const shareText = useMemo(() => {
    if (!selected) return '';
    return [
      `${selected.checklistNumber || 'Solar commissioning checklist'} - ${selected.systemSizeKw}kW ${selected.phaseType || 'Single phase'}`,
      `Project: ${selected.projectName || '-'}`,
      `Client: ${selected.clientName || '-'}`,
      `Location: ${selected.location || '-'}`,
      `Date: ${formatDate(selected.commissioningDate)}`,
      `Status: ${selected.status || 'draft'}`
    ].join('\n');
  }, [selected]);

  const emailChecklist = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const doc = await buildChecklistPdf(selected, company);
      const filename = pdfFileName(selected);
      doc.save(filename);
      const generatedFiles = [`Commissioning checklist: ${filename}`];
      let downloadedPdfCount = 1;
      if (selected.status === 'completed') {
        const certificateDoc = await buildCompletionCertificatePdf(selected, company);
        const certificateFilename = completionCertificateFileName(selected);
        certificateDoc.save(certificateFilename);
        generatedFiles.push(`Acceptance and completion certificate: ${certificateFilename}`);
        downloadedPdfCount += 1;
      } else {
        generatedFiles.push('Acceptance and completion certificate: generated after checklist status is completed');
      }
      const subject = encodeURIComponent(`${selected.checklistNumber || 'Solar Commissioning'} - ${selected.projectName || selected.clientName || 'Client'}`);
      const body = encodeURIComponent(`${shareText}\n\nPDF files generated:\n${generatedFiles.join('\n')}\n\nPlease attach the downloaded PDF file(s) before sending.`);
      const to = encodeURIComponent(selected.clientSignature?.email || '');
      window.open(`https://mail.google.com/mail/?view=cm&fs=1&to=${to}&su=${subject}&body=${body}`, '_blank', 'noopener,noreferrer');
      setMessage(`${downloadedPdfCount > 1 ? 'PDFs downloaded' : 'PDF downloaded'}. Gmail opened; attach the downloaded file(s) before sending.`);
      setError('');
    } catch {
      setError('Failed to generate PDF for Gmail.');
    } finally {
      setSaving(false);
    }
  };

  const saveChecklistPdf = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const doc = await buildChecklistPdf(selected, company);
      doc.save(pdfFileName(selected));
      setMessage('PDF generated.');
    } catch {
      setError('Failed to generate PDF.');
    } finally {
      setSaving(false);
    }
  };

  const saveCompletionCertificatePdf = async () => {
    if (!selected) return;
    if (selected.status !== 'completed') {
      setError('Set status to completed before generating the acceptance and completion certificate.');
      return;
    }
    setSaving(true);
    try {
      const doc = await buildCompletionCertificatePdf(selected, company);
      doc.save(completionCertificateFileName(selected));
      setMessage('Acceptance and completion certificate generated.');
      setError('');
    } catch {
      setError('Failed to generate acceptance and completion certificate.');
    } finally {
      setSaving(false);
    }
  };

  const viewCompletionCertificatePdf = async () => {
    if (!selected) return;
    if (selected.status !== 'completed') {
      setError('Set status to completed before viewing the acceptance and completion certificate.');
      return;
    }
    setActiveView('certificatePreview');
  };

  const shareChecklist = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const doc = await buildChecklistPdf(selected, company);
      const blob = doc.output('blob');
      const file = new File([blob], pdfFileName(selected), { type: 'application/pdf' });
      if (navigator.share) {
        if (navigator.canShare?.({ files: [file] })) {
          await navigator.share({ title: selected.checklistNumber || 'Solar Commissioning', text: shareText, files: [file] });
        } else {
          await navigator.share({ title: selected.checklistNumber || 'Solar Commissioning', text: shareText });
        }
        return;
      }
      await navigator.clipboard?.writeText(shareText);
      doc.save(pdfFileName(selected));
      setMessage('PDF downloaded and checklist summary copied. You can attach it to Messenger, email, or any app.');
    } catch {
      setMessage('Share was cancelled.');
    } finally {
      setSaving(false);
    }
  };

  const previewChecklist = () => {
    if (!selected) return null;
    const isCertificatePreview = activeView === 'certificatePreview';
    return (
      <div className="flex h-[calc(100svh-8rem)] min-h-[560px] flex-col overflow-hidden rounded-lg bg-gray-100 sm:h-[calc(100vh-9rem)]">
        <div className="flex flex-col gap-2 border-b bg-white px-3 py-2 print:hidden md:flex-row md:items-center md:justify-between">
          <button type="button" onClick={() => setActiveView('edit')} className="min-h-10 rounded border px-3 py-2 text-sm hover:bg-gray-100">{readOnly ? 'Back to details' : 'Back to edit'}</button>
          <p className="text-sm text-gray-500">{isCertificatePreview ? 'A4 certificate preview' : 'A4 PDF preview'}</p>
          <div className="grid grid-cols-2 gap-2 md:flex">
            {!isCertificatePreview && (
              <button type="button" onClick={() => printChecklist(selected, company)} className="min-h-10 rounded border px-3 py-2 text-sm hover:bg-gray-100">Print</button>
            )}
            <button type="button" onClick={isCertificatePreview ? saveCompletionCertificatePdf : saveChecklistPdf} disabled={saving} className="min-h-10 rounded border px-3 py-2 text-sm hover:bg-gray-100 disabled:opacity-60">
              {isCertificatePreview ? 'Save Certificate PDF' : 'Save as PDF'}
            </button>
            {previewPdfUrl && (
              <a href={previewPdfUrl} target="_blank" rel="noreferrer" className="col-span-2 min-h-10 rounded border px-3 py-2 text-center text-sm hover:bg-gray-100 md:col-span-1">Open PDF</a>
            )}
          </div>
        </div>
        <div className="flex flex-1 items-center justify-center overflow-auto p-2 sm:p-4">
          {previewLoading && <p className="rounded border bg-white px-4 py-3 text-sm text-gray-600">Building A4 preview...</p>}
          {!previewLoading && previewPdfUrl && (
            <iframe
              title={isCertificatePreview ? `${selected.checklistNumber || 'Solar commissioning'} certificate preview` : selected.checklistNumber || 'Solar commissioning A4 preview'}
              src={`${previewPdfUrl}#toolbar=1&navpanes=0&view=FitH`}
              className="h-full min-h-[680px] w-full min-w-[320px] rounded border bg-white shadow"
            />
          )}
        </div>
      </div>
    );
  };

  if (activeView === 'preview' || activeView === 'certificatePreview') return previewChecklist();

  if (activeView === 'edit' && selected) {
    return (
      <div className="space-y-4">
        {error && <p className="text-sm text-red-600">{error}</p>}
        {message && <p className="text-sm text-green-700">{message}</p>}
        <div className="sticky top-0 z-20 flex flex-col gap-3 rounded border bg-gray-50 p-3 shadow-sm lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <h3 className="font-semibold">Solar Commissioning {selected.checklistNumber}</h3>
            <p className="text-sm text-gray-500">{selected.systemSizeKw}kW {selected.phaseType || 'Single phase'} checklist</p>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-5 lg:flex lg:flex-wrap">
            <button type="button" onClick={() => setActiveView('preview')} className="min-h-10 rounded border px-3 py-2 text-sm hover:bg-gray-100">Preview</button>
            <button type="button" onClick={() => printChecklist(selected, company)} className="min-h-10 rounded border px-3 py-2 text-sm hover:bg-gray-100">Print</button>
            <button type="button" onClick={saveChecklistPdf} disabled={saving} className="min-h-10 rounded border px-3 py-2 text-sm hover:bg-gray-100 disabled:opacity-60">Save PDF</button>
            <button type="button" onClick={viewCompletionCertificatePdf} disabled={saving || selected.status !== 'completed'} title={selected.status === 'completed' ? 'View certificate' : 'Set status to completed first'} className="min-h-10 rounded border px-3 py-2 text-sm hover:bg-gray-100 disabled:opacity-60">View Certificate</button>
            <button type="button" onClick={saveCompletionCertificatePdf} disabled={saving || selected.status !== 'completed'} title={selected.status === 'completed' ? 'Download certificate' : 'Set status to completed first'} className="min-h-10 rounded border px-3 py-2 text-sm hover:bg-gray-100 disabled:opacity-60">Certificate PDF</button>
            {!employeeMode && <button type="button" onClick={emailChecklist} disabled={saving} className="min-h-10 rounded border px-3 py-2 text-sm hover:bg-gray-100 disabled:opacity-60">Email</button>}
            {!employeeMode && <button type="button" onClick={shareChecklist} className="min-h-10 rounded border px-3 py-2 text-sm hover:bg-gray-100">Share</button>}
            {!readOnly && <button type="button" onClick={saveChecklist} disabled={saving || (employeeMode && uploadingPhotos)} className="min-h-10 rounded bg-indigo-600 px-3 py-2 text-sm text-white disabled:opacity-60">{saving ? 'Saving...' : 'Save'}</button>}
            {!employeeMode && <button type="button" onClick={removeChecklist} disabled={saving} className="min-h-10 rounded border border-red-200 px-3 py-2 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50">Delete</button>}
            <button type="button" onClick={() => setActiveView('list')} className="min-h-10 rounded border px-3 py-2 text-sm hover:bg-gray-100">Close</button>
          </div>
        </div>

        {employeeMode && <p className="text-sm text-gray-600">{readOnly ? 'This form has been saved. Employees can view and print saved forms; only management can edit or delete them.' : 'Complete the form before saving. Once saved, only management can edit or delete it.'}</p>}
        <fieldset disabled={editorLocked} className="min-w-0 space-y-4">
        <section className="space-y-3 rounded border bg-white p-3 sm:p-4">
          <h4 className="font-semibold">Project details</h4>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
            <Field label="Project name" value={selected.projectName} onChange={(value) => patchSelected('projectName', value)} />
            <Field label="Client name" value={selected.clientName} onChange={(value) => patchSelected('clientName', value)} />
            <Field label="Location" value={selected.location} onChange={(value) => patchSelected('location', value)} />
            <Field label="Date" type="date" value={selected.commissioningDate} onChange={(value) => patchSelected('commissioningDate', value)} />
            <Field label="Installer name" value={selected.installerName} onChange={(value) => patchSelected('installerName', value)} />
            <Field label="System size">
              <select value={selected.systemSizeKw} onChange={(event) => patchSelected('systemSizeKw', Number(event.target.value))} className="min-h-11 w-full rounded border px-3 py-2 text-base sm:text-sm">
                {SYSTEM_SIZES.map((size) => <option key={size} value={size}>{size}kW</option>)}
              </select>
            </Field>
            <Field label="Phase" value={selected.phaseType} onChange={(value) => patchSelected('phaseType', value)} />
            <Field label="Battery" value={selected.batteryDescription} onChange={(value) => patchSelected('batteryDescription', value)} />
            <Field label="Status">
              <select value={selected.status || 'draft'} onChange={(event) => patchSelected('status', event.target.value)} className="min-h-11 w-full rounded border px-3 py-2 text-base sm:text-sm">
                <option value="draft">draft</option>
                <option value="completed">completed</option>
                <option value="cancelled">cancelled</option>
              </select>
            </Field>
          </div>
        </section>

        <section className="space-y-4 rounded border bg-white p-3 sm:p-4">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h4 className="font-semibold">System equipment</h4>
              <p className="text-xs text-gray-500">Panel kW is calculated from panel watts and pieces. If pieces is blank, series x parallel is used.</p>
            </div>
            <div className="rounded bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800">
              Panels total: {normalizePanelDetails(selected.panelDetails).totalKw || 0}kW
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-6">
            <Field label="Panel brand" value={selected.panelDetails?.brand} onChange={(value) => patchSelected('panelDetails.brand', value)} />
            <Field label="Panel watts (W)" type="number" value={selected.panelDetails?.wattageW} onChange={(value) => patchSelected('panelDetails.wattageW', value)} />
            <Field label="Panels pcs" type="number" value={selected.panelDetails?.quantity} onChange={(value) => patchSelected('panelDetails.quantity', value)} />
            <Field label="Series" type="number" value={selected.panelDetails?.series} onChange={(value) => patchSelected('panelDetails.series', value)} />
            <Field label="Parallel" type="number" value={selected.panelDetails?.parallel} onChange={(value) => patchSelected('panelDetails.parallel', value)} />
            <Field label="Calculated kW" value={normalizePanelDetails(selected.panelDetails).totalKw || 0} onChange={() => {}}>
              <input readOnly value={`${normalizePanelDetails(selected.panelDetails).totalKw || 0} kW`} className="min-h-11 w-full rounded border bg-gray-50 px-3 py-2 text-base text-gray-700 sm:text-sm" />
            </Field>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
            <Field label="Inverter brand" value={selected.inverterDetails?.brand} onChange={(value) => patchSelected('inverterDetails.brand', value)} />
            <Field label="Inverter model" value={selected.inverterDetails?.model} onChange={(value) => patchSelected('inverterDetails.model', value)} />
            <Field label="Battery brand" value={selected.batteryDetails?.brand} onChange={(value) => patchSelected('batteryDetails.brand', value)} />
            <Field label="Battery model" value={selected.batteryDetails?.model} onChange={(value) => patchSelected('batteryDetails.model', value)} />
            <Field label="Battery voltage" value={selected.batteryDetails?.voltage} onChange={(value) => patchSelected('batteryDetails.voltage', value)} />
            <Field label="Battery Ah" type="number" value={selected.batteryDetails?.ah} onChange={(value) => patchSelected('batteryDetails.ah', value)} />
            <Field label="Battery pcs" type="number" value={selected.batteryDetails?.quantity} onChange={(value) => patchSelected('batteryDetails.quantity', value)} />
            <Field label="Battery summary" value={selected.batteryDescription} onChange={(value) => patchSelected('batteryDescription', value)} />
          </div>

          {!readOnly && <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_auto] lg:items-end">
            <Field label="System photos">
              <input type="file" accept="image/*" multiple onChange={handleSystemPhotoChange} className="min-h-11 w-full rounded border px-3 py-2 text-base sm:text-sm" />
            </Field>
            <button type="button" onClick={clearSystemPhotos} disabled={!normalizeSystemPhotos(selected).length} className="min-h-10 rounded border px-3 py-2 text-sm hover:bg-gray-100 disabled:opacity-50">Remove all photos</button>
          </div>}
          {uploadingPhotos && <p className="text-sm text-gray-500">Processing photos...</p>}
          {normalizeSystemPhotos(selected).length > 0 && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {normalizeSystemPhotos(selected).map((photo, index) => (
                <div key={`${photo.slice(0, 32)}-${index}`} className="rounded border bg-gray-50 p-2">
                  <a href={photo} target="_blank" rel="noreferrer" className="block w-fit">
                    <img src={photo} alt={`Solar system ${index + 1}`} className="max-h-72 max-w-full rounded border bg-white object-contain" />
                  </a>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <span className="text-xs font-medium text-gray-500">Photo {index + 1}</span>
                    {!readOnly && <button type="button" onClick={() => removeSystemPhoto(index)} className="min-h-9 rounded border border-red-200 px-3 py-1 text-xs text-red-700 hover:bg-red-50">Remove</button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="space-y-4 rounded border bg-white p-3 sm:p-4">
          <h4 className="font-semibold">Commissioning checklist</h4>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            {(selected.sections || []).map((section, sectionIndex) => (
              <div key={section.title || sectionIndex} className="rounded border">
                <h5 className="border-b bg-gray-50 px-3 py-2 text-sm font-semibold">{sectionIndex + 1}. {section.title}</h5>
                <div className="divide-y">
                  {(section.items || []).map((item, itemIndex) => (
                    <div key={`${sectionIndex}-${itemIndex}`} className="space-y-2 p-3">
                      <label className="flex items-start gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={Boolean(item.checked)}
                          onChange={(event) => updateChecklistItem(sectionIndex, itemIndex, 'checked', event.target.checked)}
                          className="mt-0.5 h-5 w-5 shrink-0"
                        />
                        <span>{displayChecklistItemLabel(section, item, selected.batteryDescription)}</span>
                      </label>
                      <input
                        type="text"
                        value={item.note || ''}
                        onChange={(event) => updateChecklistItem(sectionIndex, itemIndex, 'note', event.target.value)}
                        placeholder="Optional note"
                        className="min-h-11 w-full rounded border px-3 py-2 text-base sm:text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <Field label="General notes" value={selected.notes} onChange={(value) => patchSelected('notes', value)} textarea />
          <Field label="Punch-list / Pending Items" value={selected.punchList} onChange={(value) => patchSelected('punchList', value)} textarea />
        </section>

        <section className="space-y-4 rounded border bg-white p-3 sm:p-4">
          <h4 className="font-semibold">Electronic signatures</h4>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="space-y-3">
              <Field label="Client / customer name" value={selected.clientSignature?.name || ''} onChange={(value) => patchSelected('clientSignature.name', value)} />
              <SignaturePad readOnly={editorLocked} label="Client signature" value={selected.clientSignature?.signatureDataUrl || ''} onChange={(value) => patchSignatureImage('clientSignature', value)} />
            </div>
            <div className="space-y-3">
              <Field label="Installer / iConnect representative" value={selected.installerSignature?.name || ''} onChange={(value) => patchSelected('installerSignature.name', value)} />
              <SignaturePad readOnly={editorLocked} label="Installer signature" value={selected.installerSignature?.signatureDataUrl || ''} onChange={(value) => patchSignatureImage('installerSignature', value)} />
            </div>
          </div>
        </section>
        </fieldset>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && <p className="text-sm text-red-600">{error}</p>}
      {message && <p className="text-sm text-green-700">{message}</p>}
      <div className="grid grid-cols-1 gap-3 rounded border bg-gray-50 p-3 lg:grid-cols-[1fr_auto] lg:items-end">
        <Field label="Search checklist, client, project, location, installer" value={search} onChange={setSearch} />
        <button type="button" onClick={loadChecklists} className="min-h-10 rounded bg-indigo-600 px-4 py-2 text-sm text-white">Search</button>
        <div className="lg:col-span-2">
          <p className="mb-2 text-xs font-medium text-gray-500">New single-phase commissioning checklist</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            {SYSTEM_SIZES.map((size) => (
              <button
                key={size}
                type="button"
                onClick={() => newChecklist(size)}
                disabled={Boolean(creatingSize)}
                className="min-h-10 rounded bg-emerald-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-60"
              >
                {creatingSize === size ? 'Creating...' : `${size}kW`}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-3 md:hidden">
        {loading && <p className="rounded border bg-white p-4 text-sm text-gray-500">Loading solar commissioning checklists...</p>}
        {!loading && checklists.length === 0 && <p className="rounded border bg-white p-4 text-sm text-gray-500">No solar commissioning checklists found.</p>}
        {checklists.map((row) => (
          <div key={row._id} className="rounded border bg-white p-3 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-gray-900">{row.checklistNumber}</p>
                <p className="truncate text-sm text-gray-700">{row.clientName || '-'}</p>
                <p className="truncate text-xs text-gray-500">{row.projectName || row.location || '-'}</p>
              </div>
              <span className="shrink-0 rounded bg-gray-100 px-2 py-1 text-xs capitalize text-gray-700">{row.status || 'draft'}</span>
            </div>
            <div className="mt-3 flex items-center justify-between gap-3 text-sm text-gray-600">
              <span>{row.systemSizeKw || 6}kW {row.phaseType || 'Single phase'}</span>
              <span>{formatDate(row.commissioningDate)}</span>
            </div>
            <button type="button" onClick={() => openChecklist(row._id)} className="mt-3 min-h-10 w-full rounded border px-3 py-2 text-sm hover:bg-gray-100">Open</button>
          </div>
        ))}
      </div>

      <div className="hidden overflow-auto rounded border md:block">
        <table className="w-full min-w-[760px] text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-2 text-left">Checklist No.</th>
              <th className="p-2 text-left">Client</th>
              <th className="p-2 text-left">Project</th>
              <th className="p-2 text-left">System</th>
              <th className="p-2 text-left">Date</th>
              <th className="p-2 text-left">Status</th>
              <th className="w-28 p-2" />
            </tr>
          </thead>
          <tbody>
            {checklists.map((row) => (
              <tr key={row._id} className="border-t hover:bg-gray-50">
                <td className="p-2 font-medium">{row.checklistNumber}</td>
                <td className="p-2">{row.clientName || '-'}</td>
                <td className="p-2">{row.projectName || '-'}</td>
                <td className="p-2">{row.systemSizeKw || 6}kW {row.phaseType || 'Single phase'}</td>
                <td className="p-2">{formatDate(row.commissioningDate)}</td>
                <td className="p-2 capitalize">{row.status || 'draft'}</td>
                <td className="p-2 text-right">
                  <button type="button" onClick={() => openChecklist(row._id)} className="rounded border px-3 py-1.5 hover:bg-gray-100">Open</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && checklists.length === 0 && <p className="p-4 text-sm text-gray-500">No solar commissioning checklists found.</p>}
        {loading && <p className="p-4 text-sm text-gray-500">Loading solar commissioning checklists...</p>}
      </div>
    </div>
  );
}
